/**
 * Vorlagen-Variablen — geteilte, reine Helfer für Web (Sende-Dialog/Editor) und
 * Worker (Smart-Send-Rendering von Freeform-Text).
 *
 * WhatsApp-Vorlagen nutzen positionale Platzhalter {{1}}, {{2}}, … im Body. Pro Position
 * definiert eine `VariableDef`, woher der Wert beim Senden kommt:
 *   - source 'CONTACT' (Typ A): automatisch aus dem Kontakt (Name/Vorname/…)
 *   - source 'INPUT'   (Typ B): manuelle Eingabe beim Senden
 */

export type TemplateVarSource = 'CONTACT' | 'INPUT';

export type ContactField = 'fullName' | 'firstName' | 'lastName' | 'phone' | 'email';

export interface VariableDef {
  /** 1-basierte Position im Body ({{index}}). */
  index: number;
  /** Anzeigelabel im Dialog/Editor. */
  label: string;
  source: TemplateVarSource;
  /** Nur bei source 'CONTACT': welches Kontaktfeld eingesetzt wird. */
  contactField?: ContactField;
  /** Beispielwert für die Meta-Einreichung (Pflicht zum Einreichen). */
  example?: string;
}

/** Minimaler Kontakt-Ausschnitt, der zum Auflösen der A-Variablen genügt. */
export interface ContactLike {
  displayName: string;
  phones?: string[];
  emails?: string[];
}

export const CONTACT_FIELD_LABELS: Record<ContactField, string> = {
  fullName: 'Voller Name',
  firstName: 'Vorname',
  lastName: 'Nachname',
  phone: 'Telefon',
  email: 'E-Mail',
};

/** Sinnvolle, editierbare Default-Beispielwerte für Profil-(CONTACT-)Variablen. */
export const CONTACT_FIELD_EXAMPLES: Record<ContactField, string> = {
  fullName: 'Max Mustermann',
  firstName: 'Max',
  lastName: 'Mustermann',
  phone: '+49 351 32029801',
  email: 'max@example.de',
};

/** Default-Beispielwert für ein Profil-Kontaktfeld (für Meta-Einreichung). */
export function defaultExampleFor(field: ContactField): string {
  return CONTACT_FIELD_EXAMPLES[field] ?? '';
}

/**
 * Default-Namespace-Präfix für neue Vorlagen-Slugs. Liefert Wiedererkennbarkeit
 * im Meta Business Manager und in Logs. Kann pro Aufruf überschrieben werden
 * (z.B. für Test-/Migrations-Szenarien).
 */
export const TEMPLATE_SLUG_NAMESPACE = 'kalkuel';

/** Meta-Limit für template name: 512 Zeichen — wir kappen defensiv bei 60. */
export const TEMPLATE_SLUG_MAX_LENGTH = 60;

/**
 * Transliteriert verbreitete Nicht-ASCII-Buchstaben in ASCII-Äquivalente, bevor
 * der Slugifier nicht-passende Zeichen zu `_` ersetzt. So wird aus „Begrüßung"
 * → „begruessung" statt „begr_ung". Deckt deutsche Umlaute + häufige Akzente ab.
 */
function transliterate(input: string): string {
  const map: Record<string, string> = {
    ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss',
    Ä: 'ae', Ö: 'oe', Ü: 'ue',
    à: 'a', á: 'a', â: 'a', ã: 'a', å: 'a',
    è: 'e', é: 'e', ê: 'e', ë: 'e',
    ì: 'i', í: 'i', î: 'i', ï: 'i',
    ò: 'o', ó: 'o', ô: 'o', õ: 'o',
    ù: 'u', ú: 'u', û: 'u',
    ñ: 'n', ç: 'c',
  };
  let out = '';
  for (const ch of input) out += map[ch] ?? ch;
  return out;
}

/**
 * Kern-Slug-Erzeugung ohne Namespace-Präfix. Lowercase, Transliteration,
 * nur [a-z0-9_], Mehrfach-`_` kollabiert, an den Rändern getrimmt. Leerer
 * Input → leerer String (Aufrufer entscheidet über Default).
 */
function slugifyCore(title: string): string {
  return transliterate(title ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Erzeugt einen Meta-kompatiblen technischen Vorlagen-Slug aus einem Anzeigenamen.
 * Default-Schema: `<namespace>_<slug>` (z.B. „Lead Begrüßung" → „kalkuel_lead_begruessung")
 * — sorgt für Wiedererkennbarkeit im Meta Business Manager.
 *
 * Optionen:
 *  - `namespace`: Präfix (Default `TEMPLATE_SLUG_NAMESPACE`). Leerer String → kein Präfix.
 *  - `maxLength`: harte Obergrenze in Zeichen (Default 60). Bei Überschreitung
 *    wird der Slug-Teil hinten gekappt; das Präfix bleibt erhalten.
 *
 * Kollisions-Suffixe (_2, _3, …) werden NICHT hier vergeben — das passiert
 * serverseitig gegen den Unique-Constraint.
 */
export function buildTemplateSlug(
  title: string,
  opts?: { namespace?: string; maxLength?: number },
): string {
  const namespace = opts?.namespace ?? TEMPLATE_SLUG_NAMESPACE;
  const maxLength = opts?.maxLength ?? TEMPLATE_SLUG_MAX_LENGTH;
  const core = slugifyCore(title) || 'vorlage';
  const prefix = namespace ? `${slugifyCore(namespace)}_` : '';
  const full = `${prefix}${core}`;
  if (full.length <= maxLength) return full;
  // Slug-Teil hinten kappen, am letzten `_` schneiden (sauberere Wortgrenze),
  // Präfix bleibt erhalten. Mindestens den Präfix + 'vorlage'-Fallback liefern.
  const available = Math.max(maxLength - prefix.length, 'vorlage'.length);
  let truncated = core.slice(0, available);
  const lastUnderscore = truncated.lastIndexOf('_');
  if (lastUnderscore > 8) truncated = truncated.slice(0, lastUnderscore);
  return `${prefix}${truncated}`.replace(/_+$/g, '');
}

/**
 * Rückwärtskompatibler Alias mit Default-Verhalten (Namespace `kalkuel`, max 60).
 * Bestehende Aufrufer (POST/PATCH-Routen) bleiben unverändert; das Schema-Update
 * greift transparent.
 */
export function slugifyTemplateName(title: string): string {
  return buildTemplateSlug(title);
}

/**
 * Zerlegt einen Anzeigenamen heuristisch in Vor-/Nachname: erstes Wort = Vorname,
 * Rest = Nachname. Bei nur einem Wort ist Vorname = ganzes Wort, Nachname leer.
 */
export function splitName(displayName: string): { firstName: string; lastName: string } {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

/** Löst ein einzelnes A-Kontaktfeld zu seinem String-Wert auf (leer, wenn nicht vorhanden). */
export function resolveContactVar(contact: ContactLike | null | undefined, field: ContactField): string {
  if (!contact) return '';
  switch (field) {
    case 'fullName':
      return contact.displayName ?? '';
    case 'firstName':
      return splitName(contact.displayName ?? '').firstName;
    case 'lastName':
      return splitName(contact.displayName ?? '').lastName;
    case 'phone':
      return contact.phones?.[0] ?? '';
    case 'email':
      return contact.emails?.[0] ?? '';
    default:
      return '';
  }
}

/**
 * Liest `variableDefs` aus dem (JSON-)Feld der Vorlage tolerant in ein typisiertes Array.
 * Gibt [] zurück, wenn nichts/Unbrauchbares hinterlegt ist (Legacy-Vorlagen).
 */
export function parseVariableDefs(raw: unknown): VariableDef[] {
  if (!Array.isArray(raw)) return [];
  const defs: VariableDef[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const index = typeof o.index === 'number' ? o.index : Number(o.index);
    if (!Number.isInteger(index) || index < 1) continue;
    const source: TemplateVarSource = o.source === 'CONTACT' ? 'CONTACT' : 'INPUT';
    const def: VariableDef = {
      index,
      label: typeof o.label === 'string' && o.label ? o.label : `Variable ${index}`,
      source,
    };
    if (source === 'CONTACT' && typeof o.contactField === 'string') {
      def.contactField = o.contactField as ContactField;
    }
    if (typeof o.example === 'string') {
      def.example = o.example;
    }
    defs.push(def);
  }
  return defs;
}

/**
 * Baut die Anfangswerte für den Sende-Dialog: A-Variablen aus dem Kontakt vorbefüllt,
 * B-Variablen leer. Schlüssel = 1-basierter Index als String.
 */
export function buildInitialVars(
  defs: VariableDef[],
  contact: ContactLike | null | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const def of defs) {
    out[String(def.index)] =
      def.source === 'CONTACT' && def.contactField
        ? resolveContactVar(contact, def.contactField)
        : '';
  }
  return out;
}

/**
 * Ersetzt {{1}}, {{2}}, … im Body durch die Werte aus `orderedVars` (0-basiertes Array,
 * Position n → {{n+1}}). Nicht belegte Platzhalter bleiben als leerer String.
 */
export function renderBody(body: string, orderedVars: string[]): string {
  return (body ?? '').replace(/\{\{\s*(\d+)\s*\}\}/g, (_m, num: string) => {
    const idx = Number(num) - 1;
    return orderedVars[idx] ?? '';
  });
}

/** Findet alle distinkten Platzhalter-Indizes ({{n}}) im Body, aufsteigend sortiert. */
export function extractPlaceholderIndices(body: string): number[] {
  const set = new Set<number>();
  for (const m of (body ?? '').matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
    const n = Number(m[1]);
    if (Number.isInteger(n) && n >= 1) set.add(n);
  }
  return [...set].sort((a, b) => a - b);
}

export type TemplateSegment =
  | { type: 'text'; value: string }
  | { type: 'var'; index: number };

/**
 * Zerlegt den Body in der Reihenfolge in feste Text-Stücke und Platzhalter ({{n}}) —
 * Grundlage für die Inline-„Lückentext"-Darstellung (Text als Spans, Platzhalter als
 * editierbare Inline-Felder). Aufeinanderfolgende Zeichen außerhalb von Platzhaltern
 * bleiben als ein zusammenhängendes Text-Segment erhalten (inkl. Zeilenumbrüchen).
 */
export function splitTemplateBody(body: string): TemplateSegment[] {
  const src = body ?? '';
  const segments: TemplateSegment[] = [];
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let last = 0;
  for (const m of src.matchAll(re)) {
    const start = m.index ?? 0;
    if (start > last) segments.push({ type: 'text', value: src.slice(last, start) });
    const n = Number(m[1]);
    if (Number.isInteger(n) && n >= 1) {
      segments.push({ type: 'var', index: n });
    } else {
      // Ungültiger Platzhalter → als Text behandeln.
      segments.push({ type: 'text', value: m[0] });
    }
    last = start + m[0].length;
  }
  if (last < src.length) segments.push({ type: 'text', value: src.slice(last) });
  return segments;
}
